import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { useConfig } from "@/config/context"
import type { ThemeName, ThemeSetting } from "@/config/types"
import { readStorage, StorageKeys, writeStorage } from "@/utils/storage"

interface StoredSettings {
  theme: ThemeSetting
  includeReplies: boolean
  mutedWords: string[]
  hideOnionRelays: boolean
  defaultRelays: string[]
  searchRelays: string[]
  language: string
}

function initialSettings(defaultTheme: ThemeSetting, defaultLanguage: string): StoredSettings {
  const fallback: StoredSettings = {
    theme: defaultTheme,
    includeReplies: false,
    mutedWords: [],
    hideOnionRelays: true,
    defaultRelays: [],
    searchRelays: [],
    language: defaultLanguage,
  }
  const stored = readStorage<Partial<StoredSettings>>(StorageKeys.settings, {})
  return { ...fallback, ...stored }
}

interface SettingsContextValue {
  settings: StoredSettings
  resolvedTheme: ThemeName
  setTheme: (theme: ThemeSetting) => void
  setIncludeReplies: (value: boolean) => void
  setMutedWords: (words: string[]) => void
  setHideOnionRelays: (value: boolean) => void
  setDefaultRelays: (relays: string[]) => void
  setSearchRelays: (relays: string[]) => void
  setLanguage: (language: string) => void
  resetSettings: () => void
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined)

function systemTheme(): ThemeName {
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light"
  }
  return "dark"
}

function resolveTheme(setting: ThemeSetting): ThemeName {
  return setting === "system" ? systemTheme() : setting
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const [settings, setSettings] = useState<StoredSettings>(() =>
    initialSettings(config.design.theme.default, config.i18n.defaultLanguage),
  )
  const [resolvedTheme, setResolvedTheme] = useState<ThemeName>(() => resolveTheme(settings.theme))

  useEffect(() => {
    writeStorage(StorageKeys.settings, settings)
  }, [settings])

  useEffect(() => {
    const theme = resolveTheme(settings.theme)
    setResolvedTheme(theme)
    document.documentElement.dataset.theme = theme
  }, [settings.theme])

  useEffect(() => {
    if (settings.theme !== "system" || typeof window === "undefined" || !window.matchMedia) return
    const query = window.matchMedia("(prefers-color-scheme: light)")
    const listener = () => {
      const theme = systemTheme()
      setResolvedTheme(theme)
      document.documentElement.dataset.theme = theme
    }
    query.addEventListener("change", listener)
    return () => query.removeEventListener("change", listener)
  }, [settings.theme])

  const update = useCallback((patch: Partial<StoredSettings>) => {
    setSettings((current) => ({ ...current, ...patch }))
  }, [])

  const value = useMemo<SettingsContextValue>(
    () => ({
      settings,
      resolvedTheme,
      setTheme: (theme) => {
        if (!config.design.theme.allowed.includes(resolveTheme(theme))) return
        update({ theme })
      },
      setIncludeReplies: (includeReplies) => update({ includeReplies }),
      setMutedWords: (mutedWords) => update({ mutedWords }),
      setHideOnionRelays: (hideOnionRelays) => update({ hideOnionRelays }),
      setDefaultRelays: (defaultRelays) => update({ defaultRelays }),
      setSearchRelays: (searchRelays) => update({ searchRelays }),
      setLanguage: (language) => update({ language }),
      resetSettings: () =>
        setSettings(initialSettings(config.design.theme.default, config.i18n.defaultLanguage)),
    }),
    [settings, resolvedTheme, config, update],
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (!context) throw new Error("useSettings must be used inside a SettingsProvider")
  return context
}
